import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronsUpDown, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import type { MegamapProject } from "./data";

const UNASSIGNED = "— nieprzypisany —";
const NO_CLIENT = "— bez klienta —";
const NO_BRANCH = "— bez oddziału —";
const SPLIT_KEY = "powerProjects.megamap.coordinatorLoad.split";

interface Group {
  key: string;
  coordinator: string;
  client: string; // etykieta w kolumnie; w trybie łącznym „N klientów"
  branch: string;
  clients: string[]; // do filtrowania — w trybie łącznym wiersz obejmuje wiele klientów
  branches: string[];
  items: MegamapProject[];
}

type SortCol = "coordinator" | "client" | "branch" | "count";
type SortDir = "asc" | "desc";

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return `1 ${one}`;
  const last = n % 10;
  const teen = n % 100 >= 12 && n % 100 <= 14;
  return `${n} ${!teen && last >= 2 && last <= 4 ? few : many}`;
}

/** Group the visible projects by (koordynator × klient × oddział), or — with `split` off —
 * by koordynator alone, so one row is that person's whole load. A project with several
 * coordinators is counted under each of them, so the counts can sum above the project total. */
function buildGroups(rows: MegamapProject[], split: boolean): Group[] {
  const m = new Map<string, Group>();
  for (const p of rows) {
    const names = p.coordinator_names.length ? p.coordinator_names : [UNASSIGNED];
    const client = p.client_name || NO_CLIENT;
    const branch = p.branch_name || NO_BRANCH;
    for (const raw of names) {
      const coordinator = raw || UNASSIGNED;
      const key = split ? `${coordinator}|${client}|${branch}` : coordinator;
      let g = m.get(key);
      if (!g) {
        g = { key, coordinator, client, branch, clients: [], branches: [], items: [] };
        m.set(key, g);
      }
      g.items.push(p);
      if (!g.clients.includes(client)) g.clients.push(client);
      if (!g.branches.includes(branch)) g.branches.push(branch);
    }
  }
  for (const g of m.values()) {
    g.items.sort((a, b) => a.name.localeCompare(b.name, "pl"));
    if (!split) {
      // Kolumny klient/oddział są w tym trybie schowane, ale zbiory zostają — filtry
      // („tylko ENERGA") mają dalej działać, tyle że na zawartości wiersza.
      g.client = g.clients.length === 1 ? g.clients[0] : plural(g.clients.length, "klient", "klientów", "klientów");
      g.branch =
        g.branches.length === 1 ? g.branches[0] : plural(g.branches.length, "oddział", "oddziały", "oddziałów");
    }
  }
  return [...m.values()];
}

/** Sort by the picked column, then always fall back to the remaining ones so the order is stable. */
function sortGroups(groups: Group[], col: SortCol, dir: SortDir): Group[] {
  const sign = dir === "asc" ? 1 : -1;
  const txt = (a: string, b: string) => a.localeCompare(b, "pl");
  return [...groups].sort((a, b) => {
    const primary =
      col === "count" ? a.items.length - b.items.length : txt(a[col], b[col]);
    return (
      sign * primary ||
      txt(a.coordinator, b.coordinator) ||
      txt(a.client, b.client) ||
      txt(a.branch, b.branch)
    );
  });
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b, "pl"));
}

const selectCls =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-900";

function SortHeader({
  label,
  col,
  sort,
  onSort,
  align = "left",
  className = "",
}: {
  label: string;
  col: SortCol;
  sort: { col: SortCol; dir: SortDir };
  onSort: (c: SortCol) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const active = sort.col === col;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th className={`px-2 py-1.5 font-medium ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        title={`Sortuj wg: ${label}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-gray-900 dark:hover:text-gray-100 ${
          active ? "text-gray-900 dark:text-gray-100" : ""
        } ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        <Icon size={12} className={active ? "text-red-600" : "text-gray-400"} />
      </button>
    </th>
  );
}

/** Obciążenie koordynatorów w rozbiciu na parę klient–oddział, dla aktualnie
 * przefiltrowanego zbioru projektów megamapy. Wiersz rozwija się do listy projektów. */
export function CoordinatorLoadTable({ rows }: { rows: MegamapProject[] }) {
  // Podział na klienta i oddział bywa potrzebny („kto obsługuje ENERGA Toruń"), a bywa
  // szumem („ile w sumie wisi na Pileckim"). Wybór zostaje na następne wejście — to
  // preferencja widoku, nie filtr.
  const [split, setSplit] = useState<boolean>(
    () => localStorage.getItem(SPLIT_KEY) !== "0",
  );
  const [open, setOpen] = useState<string[]>([]);
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: "count", dir: "desc" });
  const [q, setQ] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [client, setClient] = useState("");
  const [branch, setBranch] = useState("");

  const setSplitMode = (value: boolean) => {
    setSplit(value);
    localStorage.setItem(SPLIT_KEY, value ? "1" : "0");
    setOpen([]); // klucze wierszy zmieniają kształt — rozwinięcia nie mają się do czego odnieść
  };

  const groups = useMemo(() => buildGroups(rows, split), [rows, split]);
  const openSet = useMemo(() => new Set(open), [open]);

  // Dropdown choices come from the whole (map-filtered) set, so a picked value never
  // disappears from its own list just because another dropdown narrowed the rows.
  const choices = useMemo(
    () => ({
      coordinators: uniqueSorted(groups.map((g) => g.coordinator)),
      clients: uniqueSorted(groups.flatMap((g) => g.clients)),
      branches: uniqueSorted(groups.flatMap((g) => g.branches)),
    }),
    [groups],
  );

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matched = groups.filter(
      (g) =>
        (!coordinator || g.coordinator === coordinator) &&
        (!client || g.clients.includes(client)) &&
        (!branch || g.branches.includes(branch)) &&
        (!needle ||
          `${g.coordinator} ${g.clients.join(" ")} ${g.branches.join(" ")}`.toLowerCase().includes(needle) ||
          g.items.some((p) => p.name.toLowerCase().includes(needle))),
    );
    // Sortowanie po schowanej kolumnie nie ma jak być widoczne — wraca na koordynatora.
    const col = !split && (sort.col === "client" || sort.col === "branch") ? "coordinator" : sort.col;
    return sortGroups(matched, col, sort.dir);
  }, [groups, q, coordinator, client, branch, sort, split]);

  const toggle = (key: string) =>
    setOpen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const onSort = (col: SortCol) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: col === "count" ? "desc" : "asc" },
    );

  const filtersOn = !!(q.trim() || coordinator || client || branch);
  const clearFilters = () => {
    setQ("");
    setCoordinator("");
    setClient("");
    setBranch("");
  };

  const coordinators = new Set(visible.map((g) => g.coordinator)).size;
  const maxCount = visible.reduce((m, g) => Math.max(m, g.items.length), 0);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <h2 className="text-sm font-semibold">
          {split ? "Koordynatorzy wg klienta i oddziału" : "Koordynatorzy — łącznie"}
        </h2>
        <span className="text-xs text-gray-500">
          {visible.length}
          {filtersOn ? ` / ${groups.length}` : ""} {split ? "par klient–oddział" : "wierszy"} · {coordinators}{" "}
          koordynatorów · zgodnie z filtrami mapy
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <div className="relative min-w-[12rem] flex-1">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Szukaj: koordynator, klient, oddział, projekt…"
            className="w-full rounded-md border border-gray-300 bg-white py-1 pl-7 pr-2 text-xs dark:border-gray-600 dark:bg-gray-900"
          />
        </div>
        <select className={selectCls} value={coordinator} onChange={(e) => setCoordinator(e.target.value)}>
          <option value="">Wszyscy koordynatorzy</option>
          {choices.coordinators.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className={selectCls} value={client} onChange={(e) => setClient(e.target.value)}>
          <option value="">Wszyscy klienci</option>
          {choices.clients.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className={selectCls} value={branch} onChange={(e) => setBranch(e.target.value)}>
          <option value="">Wszystkie oddziały</option>
          {choices.branches.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        {/* Przełącznik widoku, nie filtr — dlatego stoi osobno i nie gaśnie przy „wyczyść". */}
        <div
          className="inline-flex overflow-hidden rounded-md border border-gray-300 text-xs dark:border-gray-600"
          role="group"
          title="Rozbicie na klienta i oddział albo jeden wiersz na koordynatora"
        >
          {[
            { value: true, label: "Klient i oddział" },
            { value: false, label: "Łącznie" },
          ].map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => setSplitMode(opt.value)}
              aria-pressed={split === opt.value}
              className={`px-2 py-1 transition-colors ${
                split === opt.value
                  ? "bg-red-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {filtersOn && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700"
          >
            <X size={12} /> wyczyść
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="px-3 py-4 text-sm text-gray-500">
          {groups.length === 0 ? "Brak projektów przy obecnych filtrach." : "Brak wierszy pasujących do filtrów tabeli."}
        </p>
      ) : (
        <div className="max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100 text-xs text-gray-500 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)] dark:bg-gray-900 dark:text-gray-400">
              <tr>
                <th className="w-8" />
                <SortHeader label="Koordynator" col="coordinator" sort={sort} onSort={onSort} />
                {split && <SortHeader label="Klient" col="client" sort={sort} onSort={onSort} />}
                {split && <SortHeader label="Oddział" col="branch" sort={sort} onSort={onSort} />}
                <SortHeader label="Projekty" col="count" sort={sort} onSort={onSort} align="right" className="w-40" />
              </tr>
            </thead>
            <tbody>
              {visible.map((g, i) => {
                const isOpen = openSet.has(g.key);
                const share = maxCount > 0 ? (g.items.length / maxCount) * 100 : 0;
                return [
                  <tr
                    key={g.key}
                    onClick={() => toggle(g.key)}
                    className={`cursor-pointer border-t border-gray-200 hover:bg-red-50/50 dark:border-gray-700 dark:hover:bg-gray-700/40 ${
                      isOpen
                        ? "bg-red-50/60 dark:bg-gray-700/30"
                        : i % 2 === 1
                          ? "bg-gray-50/70 dark:bg-gray-900/20"
                          : ""
                    }`}
                  >
                    <td className="pl-2">
                      <ChevronRight
                        size={14}
                        className={`text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                      />
                    </td>
                    <td className="px-2 py-2 font-medium text-gray-900 dark:text-gray-100">
                      {g.coordinator}
                      {/* W trybie łącznym kolumny klient/oddział znikają, ale „z czego" nadal
                          jest potrzebne — stąd jedna linijka podsumowania pod nazwiskiem. */}
                      {!split && (
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {g.client} · {g.branch}
                        </span>
                      )}
                    </td>
                    {split && <td className="px-2 py-2 text-gray-600 dark:text-gray-300">{g.client}</td>}
                    {split && <td className="px-2 py-2 text-gray-600 dark:text-gray-300">{g.branch}</td>}
                    <td className="px-2 py-2">
                      {/* Bar makes the load comparable at a glance; the number stays exact. */}
                      <div className="flex items-center justify-end gap-2">
                        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                          <span className="block h-full rounded-full bg-red-500/70" style={{ width: `${share}%` }} />
                        </span>
                        <span className="w-6 text-right font-semibold tabular-nums">{g.items.length}</span>
                      </div>
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${g.key}-items`} className="bg-gray-50/60 dark:bg-gray-900/40">
                      <td />
                      <td colSpan={split ? 4 : 2} className="px-2 pb-2 pt-1">
                        <ul className="space-y-0.5">
                          {g.items.map((p) => (
                            <li key={p.id} className="text-xs">
                              <Link
                                to={`/power-projects/${p.project_id}`}
                                className="text-red-600 hover:underline dark:text-red-400"
                              >
                                {p.name}
                              </Link>
                              <span className="ml-2 text-gray-400">/power-projects/{p.project_id}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CoordinatorLoadTable;
